import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);

const getLocalTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const createLocalDate = (date?: string | Date | dayjs.Dayjs) => {
  const localTz = getLocalTimezone();
  if (date) return dayjs(date).tz(localTz);
  return dayjs().tz(localTz);
};

export interface ScheduleTimePickerProps {
  selectedTime: dayjs.Dayjs | null;
  onChange: (time: dayjs.Dayjs) => void;
  timezone?: string;
}

const hoursArray = Array.from({ length: 24 }, (_, i) => i);
const minutesArray = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

export const ScheduleTimePicker: React.FC<ScheduleTimePickerProps> = ({
  selectedTime,
  onChange,
  timezone: tz,
}) => {
  const localNow = createLocalDate();
  const [hours, setHours] = useState<number>(
    selectedTime ? selectedTime.hour() : localNow.hour()
  );
  const [minutes, setMinutes] = useState<number>(
    selectedTime
      ? selectedTime.minute() - (selectedTime.minute() % 5)
      : Math.floor(localNow.minute() / 5) * 5
  );

  useEffect(() => {
    const baseDate = selectedTime
      ? selectedTime.clone()
      : tz
        ? dayjs().tz(tz)
        : createLocalDate();
    const newTime = baseDate.hour(hours).minute(minutes);
    onChange(newTime);
  }, [hours, minutes]);

  return (
    <div className="time-selector broadcast-schedule-time-picker">
      <div className="time-selector-header">
        <div className="selected-time">
          <span>
            {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}
          </span>
        </div>
      </div>
      <div className="time-selector-body">
        <div className="time-selector-column">
          <div className="time-selector-label">
            <span>Hours</span>
          </div>
          <div className="time-selector-items hours custom-scrollbar">
            {hoursArray.map((hour) => (
              <div
                key={hour}
                className={`time-item ${hours === hour ? 'selected' : ''}`}
                onClick={() => setHours(hour)}
                onKeyDown={(e) => e.key === 'Enter' && setHours(hour)}
                role="button"
                tabIndex={0}
              >
                <span>{String(hour).padStart(2, '0')}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="time-selector-column">
          <div className="time-selector-label">
            <span>Minutes</span>
          </div>
          <div className="time-selector-items minutes custom-scrollbar">
            {minutesArray.map((minute) => (
              <div
                key={minute}
                className={`time-item ${minutes === minute ? 'selected' : ''}`}
                onClick={() => setMinutes(minute)}
                onKeyDown={(e) => e.key === 'Enter' && setMinutes(minute)}
                role="button"
                tabIndex={0}
              >
                <span>{String(minute).padStart(2, '0')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleTimePicker;
